import { Briefcase } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { formatCount } from '@/lib/format';
import { useTranslations } from '@/i18n/use-locale';
import { useLocalizedName } from '@/i18n/localized-name';
import { QueryGate } from '@/components/ui/query-gate';
import { useDemandSupplyQuery } from '@/lib/queries';
import { StatusBadge } from '@/components/ui/status-badge';
import { useEnumLabel } from '@/lib/labels';
import { Card } from '@/components/ui/card';
import { PageHeader } from '@/components/admin/page-header';
import { DataTable, type DataTableColumn } from '@/components/admin/data-table';
import type { DemandSupply } from '@/contracts';

export function RolesRoute() {
  const t = useTranslations('admin.rolesCatalog');
  const name = useLocalizedName();
  // Technology and governance keep the design's shorter department wording; the rest
  // use the catalogue's department names.
  const departmentLabel = useEnumLabel(
    'admin.rolesCatalog.deptShort',
    'admin.rolesCatalog.department',
  );
  const categoryLabel = useEnumLabel('admin.rolesCatalog.category');
  const demandQuery = useDemandSupplyQuery();

  const columns: ReadonlyArray<DataTableColumn<DemandSupply>> = [
    {
      key: 'role',
      header: t('column.roleFamily'),
      cell: (role) => (
        <>
          <p className="font-bold text-navy">{name(role)}</p>
          <p className="text-[11.5px] text-conc-500">{categoryLabel(role.category)}</p>
        </>
      ),
    },
    {
      key: 'department',
      header: t('column.structureDepartment'),
      cellClassName: 'font-semibold text-navy',
      cell: (role) => departmentLabel(role.department),
    },
    {
      key: 'mandated',
      header: t('column.mandated'),
      cell: (role) => (
        <StatusBadge tone={role.mandatory ? 'approved' : 'neutral'}>
          {role.mandatory ? t('mandated') : t('optional')}
        </StatusBadge>
      ),
    },
    {
      key: 'demand',
      header: t('column.orgDemand'),
      cellClassName: 'num font-bold text-navy',
      cell: (role) => formatCount(role.demand),
    },
    {
      key: 'supply',
      header: t('column.availableSupply'),
      cellClassName: 'font-bold text-navy',
      cell: (role) => formatCount(role.supply),
    },
    {
      key: 'status',
      header: t('column.status'),
      cellClassName: 'font-bold',
      cell: (role) => (
        <span className={role.shortage ? 'text-rose-600' : 'text-pitch'}>
          {role.shortage ? t('shortage') : t('covered')}
        </span>
      ),
    },
  ];

  return (
    <QueryGate query={demandQuery}>
      {(roles) => {
        return (
          <div className="space-y-6">
            <PageHeader title={t('screenTitle')} sub={t('screenHint')} />

            {/* Roles Table */}
            <Card className="overflow-hidden shadow-sm">
              <DataTable
                caption={t('screenTitle')}
                rows={roles}
                columns={columns}
                rowKey={(role) => role.specialtyId}
                empty={
                  <EmptyState icon={Briefcase} title={t('empty.title')} hint={t('empty.hint')} />
                }
              />
            </Card>
          </div>
        );
      }}
    </QueryGate>
  );
}
